<template>
  <vs-wrapper>
    <vs-text-input
      :value="searchInput"
      label="Search by project name"
      @input="handleSearchInputChangeHandler"
    >
      <template #append>
        <v-icon>search</v-icon>
      </template>
    </vs-text-input>

    <div v-if="isLoading" class="loader-container">
      <v-progress-circular
        class="loading-icon"
        :size="48"
        :width="4"
        indeterminate
        color="primary"
      />
    </div>
    <div v-if="hasLoadingError" class="">
      Error occurred while loading projects, please try again.
    </div>
    <vs-wrapper v-else-if="projectsLoaded.length > 0">
      <v-list class="projects-list">
        <v-list-item-group :value="projectPageIndex">
          <template v-for="(project, i) in projectsLoaded">
            <v-list-item v-if="project" :key="i" @click="select(project.id)">
              <v-list-item-content>
                <vs-text>{{ project.name }} #{{ project.number }}</vs-text>

                <created-info
                  :creator-sid="project.createdBy"
                  :created-time="project.created"
                  text-type="subdued"
                />
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-list-item-group>
      </v-list>
      <vs-pagination v-if="pagination" v-model="currentPage" :length="pagination.totalPages" />
    </vs-wrapper>
    <div v-else class="text-center">
      <vs-text v-if="!searchInput" type="caption">No projects found</vs-text>
      <vs-text v-else type="caption"
        >No results for <em>{{ searchInput }}</em></vs-text
      >
    </div>
  </vs-wrapper>
</template>

<script>
/**
 * ProjectSelection Component.
 */

import CreatedInfo from '../common/CreatedInfo.vue'
import VsWrapper from '@/components/vision/VsWrapper.vue'
import VsText from '@/components/vision/VsText.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsPagination from '../vision/VsPagination.vue'
import { debounce } from 'lodash'

const DEBOUNCE_WAIT_TIME = 500

export default {
  components: {
    VsTextInput,
    VsText,
    VsWrapper,
    CreatedInfo,
    VsPagination,
  },
  props: {
    searchInput: {
      type: String,
      default: () => '',
    },
    ownerId: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
      default: () => true,
    },
    hasLoadingError: {
      type: Boolean,
      default: () => false,
    },
    projects: {
      // Was `type: ProjectListResponse`, a TypeScript interface with no runtime
      // value — so this has always evaluated to `type: undefined`, which Vue
      // treats as "no type". Omitting the key is exactly equivalent. Shape is
      // ProjectListResponse from @ironsight/types.
      required: false,
      default: () => null,
    },
    selectedProjectId: {
      type: String,
      required: false,
      default: () => null,
    },
    currentIndex: {
      type: Number,
      default: () => 0,
    },
  },
  computed: {
    projectPageIndex() {
      return this.$props.currentIndex
    },
    projectsLoaded() {
      return this.$props.projects?.data ?? []
    },
    pagination() {
      return this.$props.projects?.meta ?? null
    },
    currentPage: {
      get() {
        return this.$props.currentIndex
      },
      set(value) {
        this.$emit('page-change', value)
      },
    },
  },
  methods: {
    select(projectId) {
      this.$emit('select', projectId)
    },
    handleSearchInputChangeHandler: debounce(function (search) {
      this.$emit('search', search)
    }, DEBOUNCE_WAIT_TIME),
  },
}
</script>

<style scoped>
.projects-list {
  margin: 0 -16px;
}
.loader-container {
  width: 100%;
  display: flex;
}

.loading-icon {
  margin: auto;
}
</style>
